/* eslint-disable max-lines -- one memoized render pass over the child transcript; this root refuses the new module a split would need. */
/**
 * Renders the Evidence sub-agent's CHILD harness run — a flat `ChatItem[]`
 * transcript, not the legacy `evidence.chunk` conversation shape. Owns
 * whole-timeline concerns a per-item renderer can't: collapsing contiguous
 * search rows into one group, and building a single references section from
 * every answer's citations. Chromeless — the timeline IS the UI.
 */

import type {
  ChatItem,
  CitationRegistry,
  ResolvedCitationOccurrence,
  ToolUseItem,
} from '@heidi/agents/contracts';
import {
  closeArtifactAtom,
  extractLatestTurnCopyableText,
  friendlyToolError,
  openArtifactAtom,
} from '@heidi/agents/data';
import { normalizeHeadingDepth } from '@heidi/answer-kit';
import type { VisualDxDiagnosticFlowSelections } from '@heidi/evidence';
import {
  DrugInteractionWarningBanner,
  EvidenceCitationsButton,
  EvidenceToolFallbackPill,
  findLastRenderableEvidenceChildIndex,
  isSuppressedEvidenceChildTool,
  isVisualDxAgentToolName,
  LibrarySearchBlock,
  MEDICAL_CALCULATOR_TOOL,
  ReferencesSection,
  shouldShowDrugInteractionWarning,
  usePublishEvidenceReferences,
  VisualDxAgentToolBlock,
} from '@heidi/evidence';
import type { EnrichedReference, SimpleCitation } from '@heidi/evidence/contracts';
import { logger } from '@heidi/runtime/logger';
import { cn } from '@heidi/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { Fragment, useEffect, useMemo, useRef, type JSX, type ReactNode } from 'react';

import { getWorkHostPort } from '../../../work-home/work-home-ports';
import { renderMedicalCalculatorToolUse } from '../../timeline/evidence-tool-adapters/medical-calculator';
import {
  readToolData,
  readToolError,
} from '../../timeline/evidence-tool-adapters/shared/readers';
import { EvidenceAgentAnswer } from './EvidenceAgentAnswer';
import { EvidenceImageStrip } from './EvidenceImageStrip';
import { EvidenceSearchGroup } from './EvidenceSearchGroup';
import {
  collectEvidenceImages,
  collectEvidencePayloads,
  LIBRARY_SEARCH_TOOL,
  readQuery,
  readSources,
  toSearchRow,
  toSearchStatus,
  WEB_STYLE_SEARCH_TOOLS,
} from './evidenceChildData';

export interface EvidenceChildTimelineProps {
  items: ChatItem[];
  registry: CitationRegistry;
  isStreaming?: boolean;
  sessionId?: string | null;
  /** Bumps when partner branding resolves late; forwarded to force a citation remount. */
  partnerConfigVersion?: number;
  onResolveVisualDx?: (
    approvalId: string,
    selections: VisualDxDiagnosticFlowSelections
  ) => Promise<void>;
  /**
   * The reply's action row. Rendered once settled, directly under the answer
   * (and its drug-interaction banner) and above References — the placement of
   * Evidence's own `TextResponse` action bar. Receives the answer's plain text.
   */
  renderReplyActions?: (copyableText: string | null) => ReactNode;
  /** Child run these citations belong to. Work's button opens them; standalone Evidence leaves this unset. */
  runId?: string;
  onOpenReferences?: (runIds: string[]) => void;
  /** The parent's Working row lists the searches and sources, so no search group or library block is drawn. */
  searchesInWorkingBlock?: boolean;
  className?: string;
}

/**
 * An unresolved token is whatever the model put between the tags, so it is
 * untrusted text rather than a guaranteed opaque id — search-result injection
 * could park clinical prose there. Strip it to token-shaped characters and cap
 * it before it reaches telemetry: still enough to debug a genuine dangling
 * token, never enough to carry PHI.
 */
const toLoggableToken = (token: string): string =>
  token.replace(/[^A-Za-z0-9:_-]/g, '').slice(0, 64);

/** The cite and image markup the copied answer must not carry. */
const stripCitationAndImageMarkup = (text: string): string => {
  const bridge = getWorkHostPort('bridge');
  return bridge.stripImgTags(bridge.stripCiteTags(text));
};

export const EvidenceChildTimeline = ({
  items,
  registry,
  isStreaming = false,
  sessionId = null,
  partnerConfigVersion,
  onResolveVisualDx,
  renderReplyActions,
  runId,
  onOpenReferences,
  searchesInWorkingBlock = false,
  className,
}: EvidenceChildTimelineProps): JSX.Element => {
  const citationsEnabled =
    getWorkHostPort('shell').useIsWorkEvidenceCitationsEnabled?.() ?? false;
  const inlineImagesEnabled = getWorkHostPort('shell').useIsEvidenceInlineImagesEnabled();
  const residualCapabilitiesEnabled =
    getWorkHostPort('shell').useIsEvidenceSubagentResidualCapabilitiesEnabled();
  const EvidenceAnswerPortsHost = getWorkHostPort('evidence').EvidenceAnswerPortsHost;
  const showDrugInteractionWarning = useMemo(
    () => shouldShowDrugInteractionWarning(items, { isStreaming }),
    [items, isStreaming]
  );

  // Memoized — the regex cite-parsing + O(n²) claim scoring would otherwise
  // re-run on every streaming text-delta re-render.
  const { rendered, references, unresolvedTokens, unknownImageTokens, images } =
    useMemo(() => {
      const bridge = getWorkHostPort('bridge');
      const rendered: JSX.Element[] = [];
      const occurrences: ResolvedCitationOccurrence[] = [];
      const consumedSearchIdx = new Set<number>();
      const unresolvedTokens = new Set<string>();
      const unknownImageTokens = new Set<string>();
      // No payloads when the flag is off ⇒ an empty registry: `<img>` tag hygiene
      // still runs (raw tokens must never reach Streamdown), it just resolves nothing.
      const evidencePayloads = inlineImagesEnabled ? collectEvidencePayloads(items) : [];
      const imageRegistry = bridge.buildImageRegistry(evidencePayloads);
      const placedImageIds = new Set<string>();
      // Running counts of resolved citations/placed images above, so badge and
      // figure numbering stay continuous across a multi-block answer.
      let citeBase = 0;
      let imgBase = 0;
      const lastRenderableIndex = findLastRenderableEvidenceChildIndex(
        items,
        residualCapabilitiesEnabled
      );

      items.forEach((item, index) => {
        const active = isStreaming && index === lastRenderableIndex;

        if (item.kind !== 'assistant_text' && item.kind !== 'tool_use') return;

        if (item.kind === 'assistant_text') {
          if (!item.text) return;
          const marks = bridge.buildCiteMarkers(item.text, registry, citeBase, {
            streaming: active,
          });
          citeBase += marks.occurrences.length;
          occurrences.push(...marks.occurrences);
          marks.unresolvedTokens.forEach((token) => unresolvedTokens.add(token));
          const imageMarks = bridge.buildImageMarkers(
            marks.fullText,
            imageRegistry,
            imgBase,
            {
              streaming: active,
            }
          );
          imgBase += imageMarks.placedCount;
          imageMarks.placedImageIds.forEach((id) => placedImageIds.add(id));
          // Flag off ⇒ every token is "unknown" by construction; not worth reporting.
          if (inlineImagesEnabled) {
            imageMarks.unknownImageTokens.forEach((token) =>
              unknownImageTokens.add(token)
            );
          }
          rendered.push(
            <EvidenceAgentAnswer
              key={item.id}
              markedText={imageMarks.fullText}
              citationMap={marks.citationMap as Map<number, SimpleCitation[]>}
              imageMap={imageRegistry}
              streaming={active}
              sessionId={sessionId}
              runId={citationsEnabled ? runId : undefined}
              partnerConfigVersion={partnerConfigVersion}
            />
          );
          return;
        }

        // tool_use
        if (isSuppressedEvidenceChildTool(item.name, residualCapabilitiesEnabled)) return;

        if (WEB_STYLE_SEARCH_TOOLS.has(item.name)) {
          if (consumedSearchIdx.has(index)) return;
          const group: ToolUseItem[] = [item];
          let groupEndIdx = index;
          for (let i = index + 1; i < items.length; i += 1) {
            const next = items[i];
            if (next?.kind === 'tool_use' && WEB_STYLE_SEARCH_TOOLS.has(next.name)) {
              group.push(next);
              consumedSearchIdx.add(i);
              groupEndIdx = i;
              continue;
            }
            if (
              next?.kind === 'tool_use' &&
              isSuppressedEvidenceChildTool(next.name, residualCapabilitiesEnabled)
            ) {
              consumedSearchIdx.add(i);
              continue;
            }
            break;
          }
          if (searchesInWorkingBlock) return;
          const hasTextAfter = items
            .slice(groupEndIdx + 1)
            .some((entry) => entry.kind === 'assistant_text' && entry.text);
          rendered.push(
            <EvidenceSearchGroup
              key={`search-${item.id}`}
              rows={group.map(toSearchRow)}
              isStreaming={isStreaming}
              waitingForText={isStreaming && !hasTextAfter}
              sessionId={sessionId}
            />
          );
          return;
        }

        if (item.name === LIBRARY_SEARCH_TOOL) {
          if (searchesInWorkingBlock) return;
          rendered.push(
            <LibrarySearchBlock
              key={item.id}
              query={readQuery(item)}
              sources={readSources(item)}
              status={toSearchStatus(item.status)}
            />
          );
          return;
        }

        if (isVisualDxAgentToolName(item.name)) {
          const approvalId =
            item.pendingApprovalId ??
            (item.approval?.status === 'pending' ? item.approval.approval_id : undefined);
          rendered.push(
            <VisualDxAgentToolBlock
              key={item.id}
              name={item.name}
              input={item.input}
              status={item.status}
              data={readToolData(item)}
              error={friendlyToolError(readToolError(item))}
              approvalId={approvalId}
              onResolve={onResolveVisualDx}
            />
          );
          return;
        }

        if (item.name === MEDICAL_CALCULATOR_TOOL) {
          const calculator = renderMedicalCalculatorToolUse(item);
          rendered.push(
            calculator ? (
              <Fragment key={item.id}>{calculator}</Fragment>
            ) : (
              <EvidenceToolFallbackPill key={item.id} name={item.name} active={active} />
            )
          );
          return;
        }

        // An unexpected child tool — a one-line pill so it is visible but never crashes.
        rendered.push(
          <EvidenceToolFallbackPill key={item.id} name={item.name} active={active} />
        );
      });

      return {
        rendered,
        references: bridge.buildReferences(occurrences) as EnrichedReference[],
        unresolvedTokens: Array.from(unresolvedTokens),
        unknownImageTokens: Array.from(unknownImageTokens),
        // Anchored images are omitted from the trailing strip; legacy (no
        // `image_id`) and never-anchored ones still surface there.
        images: collectEvidenceImages(evidencePayloads).filter(
          (image) => !image.image_id || !placedImageIds.has(image.image_id)
        ),
      };
    }, [
      items,
      registry,
      isStreaming,
      sessionId,
      runId,
      citationsEnabled,
      partnerConfigVersion,
      onResolveVisualDx,
      inlineImagesEnabled,
      residualCapabilitiesEnabled,
      searchesInWorkingBlock,
    ]);

  // A token can legitimately resolve late, so only warn once streaming has settled,
  // and only once per mount so a genuinely dangling token doesn't spam the logger.
  const hasWarnedUnresolvedRef = useRef(false);
  useEffect(() => {
    if (isStreaming || hasWarnedUnresolvedRef.current) return;
    if (unresolvedTokens.length === 0 && unknownImageTokens.length === 0) return;
    hasWarnedUnresolvedRef.current = true;
    if (unresolvedTokens.length > 0) {
      logger.warn('Evidence citation tokens unresolved after completion', {
        count: unresolvedTokens.length,
        tokens: unresolvedTokens.map(toLoggableToken),
      });
    }
    if (unknownImageTokens.length > 0) {
      logger.warn('Evidence image tokens unresolved after completion', {
        count: unknownImageTokens.length,
        tokens: unknownImageTokens.map(toLoggableToken),
      });
    }
  }, [isStreaming, unresolvedTokens, unknownImageTokens]);

  usePublishEvidenceReferences(citationsEnabled ? runId : undefined, references);
  const openArtifact = useAtomValue(openArtifactAtom);
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const isCitationsOpen =
    openArtifact?.type === 'evidence-references' &&
    !!runId &&
    openArtifact.runIds.includes(runId);
  const showCitationsButton =
    citationsEnabled && !!runId && !!onOpenReferences && !isStreaming;

  const replyActions =
    !isStreaming && renderReplyActions
      ? renderReplyActions(
          extractLatestTurnCopyableText(items, {
            wholeTranscript: true,
            stripCitationMarkup: stripCitationAndImageMarkup,
            normalizeHeadingDepth,
          })
        )
      : null;

  return (
    <EvidenceAnswerPortsHost>
      <div
        className={cn(
          'flex w-full flex-col gap-5',
          // Freeze in-progress loaders once settled so nothing spins forever.
          !isStreaming &&
            '[&_.animate-pulse]:animate-none [&_.animate-spin]:animate-none',
          className
        )}
      >
        {rendered}
        {images.length > 0 && <EvidenceImageStrip images={images} />}
        {showDrugInteractionWarning && <DrugInteractionWarningBanner />}
        {/* -mt-3 pulls the row to Evidence's 8px under the prose inside this gap-5 stack. */}
        {replyActions ? <div className="-mt-3">{replyActions}</div> : null}
        {showCitationsButton && runId ? (
          <EvidenceCitationsButton
            runIds={[runId]}
            isOpen={isCitationsOpen}
            hideWhenClaimed
            onOpen={(ids) => {
              if (isCitationsOpen) closeArtifact();
              else onOpenReferences?.(ids);
            }}
            className={cn('self-start', replyActions && 'mt-1')}
          />
        ) : null}
        {!citationsEnabled && references.length > 0 && (
          <ReferencesSection
            className={replyActions ? 'mt-1' : undefined}
            references={references}
            isStreaming={isStreaming}
            autoExpandOnComplete
          />
        )}
      </div>
    </EvidenceAnswerPortsHost>
  );
};
